import { GetUserDetailsResponseProto } from "@noom/noom-contracts/noom_contracts/backend/user/user_service";
import {
  SexProto_Value,
  GenderProto_Value,
} from "@noom/noom-contracts/noom_contracts/backend/user/model/sex_and_gender";
import { UserDataState } from "./redux/slices/userData";
import { SurveyAnswersState } from "./redux/slices/surveyAnswers";
import { genderConstants, sexConstants } from "./sexAndGender";
import { convertKgToLb, convertCmToFt } from "src/primitive/UnitUtils";
import { calculateAge } from "src/utils/datetime";

/**
 * Maps proto gender enum to internal gender constants
 * @param protoGender - Proto gender enum value
 * @returns Internal gender constant or undefined
 */
function mapProtoGenderToInternal(
  protoGender: GenderProto_Value
): genderConstants | undefined {
  switch (protoGender) {
    case "MALE":
      return genderConstants.male;
    case "FEMALE":
      return genderConstants.female;
    case "SELF_ID":
      return genderConstants.nonBinary;
    case "UNKNOWN":
      return genderConstants.preferNotToSay;
    default:
      return undefined;
  }
}

/**
 * Maps proto sex enum to internal sex constants
 * @param protoSex - Proto sex enum value
 * @returns Internal sex constant or undefined
 */
function mapProtoSexToInternal(
  protoSex: SexProto_Value
): sexConstants | undefined {
  switch (protoSex) {
    case "MALE":
      return sexConstants.male;
    case "FEMALE":
      return sexConstants.female;
    case "INTERSEX":
      return sexConstants.intersex;
    case "UNKNOWN":
    case "UNSPECIFIED":
    default:
      return undefined;
  }
}

/**
 * Transforms the getUserDetails API response into UserDataState and SurveyAnswersState updates
 * @param response - The API response
 * @returns Object with userData and surveyAnswers updates
 */
export function transformUserDetailsToUserData(
  response: GetUserDetailsResponseProto
): {
  userData: Partial<UserDataState>;
  surveyAnswers: Partial<SurveyAnswersState>;
} {
  const userData: Partial<UserDataState> = {};
  const surveyAnswers: Partial<SurveyAnswersState> = {};

  // Convert and set weight in userData and surveyAnswers
  if (response.weightInKg !== undefined) {
    userData.weight = Math.round(
      convertKgToLb({ mainUnitValue: response.weightInKg }).mainUnitValue
    );
    surveyAnswers.weightKg = response.weightInKg;
  }

  // Convert and set height in userData and surveyAnswers
  if (response.heightInCm !== undefined) {
    const { mainUnitValue: heightFeet, secondaryUnitValue: heightInch } =
      convertCmToFt({ mainUnitValue: response.heightInCm });
    userData.heightFeet = heightFeet;
    userData.heightInch = heightInch || 0;
    surveyAnswers.heightCm = response.heightInCm;
  }

  // Map and set gender in surveyAnswers
  if (response.gender !== undefined) {
    const internalGender = mapProtoGenderToInternal(response.gender);
    if (internalGender !== undefined) {
      surveyAnswers.gender = [internalGender];
    }
  }

  // Map and set sex in surveyAnswers
  if (response.sex !== undefined) {
    const internalSex = mapProtoSexToInternal(response.sex);
    if (internalSex !== undefined) {
      surveyAnswers.sex = [internalSex];
    }
  }

  // Set age in surveyAnswers - prefer calculated age from birth date, fallback to approximate age
  if (
    response.birthDate &&
    response.birthDate.year &&
    response.birthDate.month &&
    response.birthDate.day
  ) {
    const birthDateObj = new Date(
      response.birthDate.year,
      response.birthDate.month - 1,
      response.birthDate.day
    );
    surveyAnswers.ageYears = calculateAge(birthDateObj);
  } else if (response.approximateAge !== undefined) {
    surveyAnswers.ageYears = response.approximateAge;
  }

  // Convert and set target weight in surveyAnswers
  if (response.targetWeightInKg !== undefined) {
    surveyAnswers.idealWeightKg = response.targetWeightInKg;
    surveyAnswers.idealWeight = Math.round(
      convertKgToLb({ mainUnitValue: response.targetWeightInKg }).mainUnitValue
    );
  }

  return { userData, surveyAnswers };
}
